import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'Donor Retention for Missionaries: How to Stop Supporters From Quietly Lapsing';
const DESCRIPTION =
    "Most missionary support doesn't end with a dramatic cancellation — it fades through silence. A practical guide to the retention habits that keep recurring donors giving year after year.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover29.jpg',
            datePublished: '2026-07-28',
            dateModified: '2026-07-28',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/donor-retention-for-missionaries',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Why do missionary donors stop giving?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Rarely a dramatic decision. Most lapses happen quietly — a donor's card expires, they stop hearing updates and assume the work has changed, or their own life gets busy and giving simply falls off their radar. Silence, not disagreement, is the most common cause.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How often should I update my donors?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'A monthly cadence is the sweet spot for most missionaries — frequent enough that donors never wonder if you\'ve gone quiet, infrequent enough that it never feels like spam. A short, consistent update beats a long, rare one.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What is a healthy donor retention rate for a missionary?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'There is no official benchmark for personal missionary support, but treat any year where you retain fewer than 80-85% of recurring donors as a signal to look harder at your update rhythm and payment-failure follow-up, not just your fundraising volume.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What do I do when a recurring donation fails silently?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Treat a failed card as an urgent, personal follow-up, not a system problem to ignore. Most failures are an expired card, not a decision to stop giving — a quick, warm message asking the donor to update their payment method recovers the large majority of these gifts.",
                    },
                },
            ],
        },
    ],
};

export default function DonorRetentionForMissionaries() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/donor-retention-for-missionaries' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-donor-retention-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover29.jpg" alt="An open hand holding a small seed, representing the quiet, ongoing care that keeps a missionary's donor base growing rather than fading" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Support raising gets most of the attention, but keeping the donors you already have is what actually determines whether your support stays full year over year. Missionaries rarely lose donors to a dramatic cancellation. They lose them to silence — an update that never got sent, a card that quietly expired, a relationship that faded because nobody kept it warm. Retention is a habit, not a one-time campaign, and it's far cheaper than replacing a lapsed donor with a new one.</p>

                    <h2>Most lapses are silent, not decisions</h2>

                    <p>When a donor stops giving, it rarely follows a moment of disagreement or dissatisfaction. Far more often, it follows a stretch of silence: months without an update, so the donor quietly assumes the ministry has changed or ended, or a payment method that expired without anyone noticing. Treat every lapse as a process failure to investigate, not a verdict on your ministry — the fix is almost always structural, not personal.</p>

                    <h2>Build a consistent update rhythm — and protect it</h2>

                    <p>The single biggest lever for retention is a predictable update cadence, and monthly is the rhythm that works for most missionaries: frequent enough that donors never wonder if you've gone quiet, infrequent enough that it never feels like noise. Protect this rhythm the same way you'd protect a Sunday service — put it on a recurring calendar block rather than relying on finding time when things are slow, because things are rarely slow. A short, honest, on-time update consistently beats a long, polished one that arrives twice a year.</p>

                    <p>Vary the content so updates don't blur together: a specific story one month, a numbers-and-needs update the next, a prayer request the month after. Donors remember specifics, not generalities — see the guide on <a href="/resources/how-to-write-a-prayer-letter-people-actually-read">writing updates people actually read</a> for the structure that keeps this from feeling like a chore.</p>

                    <h2>Chase failed payments like they're urgent — because they are</h2>

                    <p>A recurring donation that fails silently is the single most recoverable form of lapsed giving, and the one most missionaries neglect. Cards expire on a schedule; roughly one in ten recurring gifts fails in a given year for purely mechanical reasons that have nothing to do with a donor's intent to keep giving. Set up a habit of reviewing failed payments at least monthly and reaching out personally and warmly — "I noticed your card on file may have expired, would you mind updating it?" — rather than letting a payment platform's generic retry emails do the work alone. This single habit recovers a meaningful share of support that would otherwise disappear without either of you noticing.</p>

                    <h2>Say thank you before you ask for anything else</h2>

                    <p>Every touchpoint that isn't a thank-you is implicitly an ask, even when you don't intend it that way. Donors who only ever hear from you when you need something eventually start reading every message as a request. Build in thank-yous that carry no ask at all — an anniversary of their first gift, a milestone your ministry reached because of their support, a simple "this made a real difference" note. See <a href="/resources/how-to-write-a-missionary-thank-you-letter">how to write a thank-you letter donors actually remember</a> for a reusable template.</p>

                    <h2>Make it easy to increase or adjust giving, not just to give</h2>

                    <p>Some donors would give more if it were simple to do — a link in an update, a note in a thank-you message, an occasional honest "here's where support currently stands." Others need the ability to pause or adjust without friction; a donor who can't easily change their gift may quietly cancel altogether rather than ask you directly. A giving page that lets supporters manage their own recurring gift removes the awkwardness from both directions and keeps more relationships intact through life changes.</p>

                    <h2>Track retention like you track new gifts</h2>

                    <p>Most missionaries track how many new donors they add and rarely track how many they keep. Once or twice a year, compare your active recurring donor count against the same period a year earlier. If retention is dropping, look first at your update consistency and failed-payment follow-up before assuming you simply need to raise more new support — plugging the leak is almost always faster than refilling the bucket.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>Why do missionary donors stop giving?</strong><br />
                    Rarely a dramatic decision — most lapses come from silence (no updates) or a quietly expired card, not disagreement with the ministry.</p>

                    <p><strong>How often should I update my donors?</strong><br />
                    Monthly works for most missionaries: frequent enough to stay present, infrequent enough to never feel like spam.</p>

                    <p><strong>What is a healthy donor retention rate for a missionary?</strong><br />
                    No official benchmark exists, but retaining fewer than 80-85% of recurring donors in a year is a signal to review your update rhythm and payment follow-up.</p>

                    <p><strong>What do I do when a recurring donation fails silently?</strong><br />
                    Follow up personally and promptly — most failures are an expired card, not a decision to stop, and a warm nudge recovers most of them.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/how-to-write-a-missionary-thank-you-letter">How to Write a Missionary Thank-You Letter Donors Actually Remember</a></li>
                        <li><a href="/resources/how-to-write-a-prayer-letter-people-actually-read">How to Write a Prayer Letter People Actually Read</a></li>
                        <li><a href="/resources/missionary-furlough-support-raising">Missionary Furlough Support Raising: How to Reconnect With Donors During Home Assignment</a></li>
                        <li><a href="/resources/how-to-track-missionary-support-raising-donors">How to Track Your Missionary Support-Raising Donors: Spreadsheet vs. Dedicated Platform</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We built this platform after watching too many missionaries lose support they'd already earned — not to a donor's change of heart, but to a card that expired quietly or an update that never got sent.</p>
                    <p><small><em>Last updated: 2026-07-28.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/donor-retention-for-missionaries" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
